import React, { useState, useRef, useEffect } from 'react';
import './HomeSlider.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Keyboard } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import CloudServicesCardsHome from '../CloudServicesCardsHome/CloudServicesCardsHome';
import CyberServicesCardsHome from '../CyberServicesCardsHome/CyberServicesCardsHome';
import CyberServicesCardsAcademy from '../CyberServicesCardsAcademy/CyberServicesCardsAcademy';
import { useTranslation } from 'react-i18next';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { GridPatternCard, GridPatternCardBody } from '../../ui/card-with-ellipsis-pattern';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const HomeSlider: React.FC = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType>();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isHoveringLeft, setIsHoveringLeft] = useState(false);
  const [isHoveringRight, setIsHoveringRight] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };

    const detectTouchDevice = () => {
      setIsTouchDevice(('ontouchstart' in window) || 
        (navigator.maxTouchPoints > 0));
    };

    const handleNavigateToSlide = (event: CustomEvent) => {
      const { slideIndex } = event.detail;
      if (swiperRef.current && typeof slideIndex === 'number') {
        swiperRef.current.slideToLoop(slideIndex);
      }
    };

    detectTouchDevice();
    window.addEventListener('resize', handleResize);
    window.addEventListener('navigateToSlide', handleNavigateToSlide as EventListener);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('navigateToSlide', handleNavigateToSlide as EventListener);
    };
  }, []);

  const slidesTitles = [
    t('features.cloudServices.title', { defaultValue: 'Cloud Services' }),
    t('features.cybersecurity.title', { defaultValue: 'Cybersecurity' }),
    t('features.itTraining.title', { defaultValue: 'IT Training' }),
  ];

  const HEADER_OFFSET = 60;
  const MAX_DESKTOP_HEIGHT = 960;
  const MIN_DESKTOP_HEIGHT = 720;

  const availableHeight = Math.max(windowHeight - HEADER_OFFSET, 400);

  const desktopHeight = Math.max(
    Math.min(availableHeight, MAX_DESKTOP_HEIGHT),
    Math.min(availableHeight, MIN_DESKTOP_HEIGHT)
  );

  const computedHeight = windowWidth > 1024 ? desktopHeight : availableHeight;

  const slideStyle = {
    minHeight: `${computedHeight}px`,
  };

  const handleSlideLeft = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleSlideRight = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  return (
    <div className="home-slider-container">
      <div className="fixed-background"></div>
      
      {!isTouchDevice && (
        <>
          <div 
            className="slider-hover-area slider-hover-area-left"
            onMouseEnter={() => setIsHoveringLeft(true)}
            onMouseLeave={() => setIsHoveringLeft(false)}
            onClick={handleSlideLeft}
          ></div>
          
          <div 
            className="slider-hover-area slider-hover-area-right"
            onMouseEnter={() => setIsHoveringRight(true)}
            onMouseLeave={() => setIsHoveringRight(false)}
            onClick={handleSlideRight}
          ></div>
        </>
      )}
      
      <div 
        className={`slider-direction-hint slider-direction-hint-left ${isHoveringLeft || isTouchDevice ? 'active' : ''}`}
        onClick={handleSlideLeft}
      >
        <IconChevronLeft className="direction-arrow" />
      </div>
      
      <div 
        className={`slider-direction-hint slider-direction-hint-right ${isHoveringRight || isTouchDevice ? 'active' : ''}`}
        onClick={handleSlideRight}
      >
        <IconChevronRight className="direction-arrow" />
      </div>
      
      <div className={`light-effect light-effect-left ${isHoveringLeft || isTouchDevice ? 'active' : ''}`}></div>
      <div className={`light-effect light-effect-right ${isHoveringRight || isTouchDevice ? 'active' : ''}`}></div>
      
      <div className="slider-bottom-grid-pattern">
        <GridPatternCard className="slider-grid-pattern">
          <GridPatternCardBody>
            <div className="slider-scroll-hint">
              <div className="scroll-icon"></div>
              <span>{t('common.swipeToExplore', { defaultValue: 'Swipe to explore' })}</span>
            </div>
          </GridPatternCardBody>
        </GridPatternCard>
      </div>
      
      <div className="slider-pagination-custom">
        {slidesTitles.map((title, index) => (
          <div 
            key={index} 
            className={`slider-pagination-item ${activeIndex === index ? 'active' : ''}`}
            onClick={() => {
              if (swiperRef.current) {
                swiperRef.current.slideToLoop(index);
              }
            }}
          >
            {title}
          </div>
        ))}
      </div>
      
      <Swiper
        modules={[Navigation, Pagination, A11y, Keyboard]}
        spaceBetween={0}
        slidesPerView={1}
        navigation={false}
        pagination={false}
        loop={true}
        keyboard={{ enabled: true }}
        className="home-slider full-width-slider"
        onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.realIndex)}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
        cssMode={windowWidth <= 768}
        style={slideStyle}
      >
        <SwiperSlide className="full-width-slide" style={slideStyle}>
          <div className="slide-content" style={slideStyle}>
            <div className="slide-wrapper">
              <div className="centered-title-dark-bg no-click">
                <h2>{t('cloudServices.title', { defaultValue: 'NextGen Cloud' })}</h2>
              </div>
              <div className="services-container">
                <CloudServicesCardsHome />
              </div>
            </div>
          </div>
        </SwiperSlide>
        <SwiperSlide className="full-width-slide" style={slideStyle}>
          <div className="slide-content" style={slideStyle}>
            <div className="slide-wrapper">
              <div className="centered-title-dark-bg no-click">
                <h2>{t('cyberServices.title', { defaultValue: 'NextGen Cybersecurity' })}</h2>
              </div>
              <div className="services-container">
                <CyberServicesCardsHome />
              </div>
            </div>
          </div>
        </SwiperSlide>
        <SwiperSlide className="full-width-slide" style={slideStyle}>
          <div className="slide-content" style={slideStyle}>
            <div className="slide-wrapper">
              <div className="centered-title-dark-bg no-click">
                <h2>{t('academy.title', { defaultValue: 'IT Monkeys Academy' })}</h2>
              </div>
              <div className="services-container">
                <CyberServicesCardsAcademy />
              </div>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
  );
};

export default HomeSlider;
